import {
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  useBreakpointValue,
  useToast
} from '@chakra-ui/react'
import { DeleteConfirmation } from '@components'
import { useSessionTranscript } from '@hooks'
import { TrashIcon } from '@icons'
import { renderSpeaker } from '@lib/transcript'
import { copyToClipboard } from '@utilities'
import React, { useState } from 'react'
import {
  DialogBody,
  DialogContainer,
  DialogFooter,
  DialogHeader
} from './dialog'

interface TranscriptModalProps {
  isOpen: boolean
  onClose: () => void
  sessionTranscriptData: any
  client: any
  sessionId: string
}

export const TranscriptModal: React.FC<TranscriptModalProps> = ({
  isOpen,
  onClose,
  sessionTranscriptData,
  client,
  sessionId
}) => {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)
  const toast = useToast()

  const modalSize = useBreakpointValue({ base: 'full', md: 'xl' })

  // The parent component passes transcript metadata via `sessionTranscriptData`.
  // Starting from that cache entry lets the modal render quickly.
  // `useSessionTranscript` will then fetch / poll the FULL transcript text only
  // while this modal is mounted.
  const {
    transcript,
    isUnavailable,
    isProcessing,
    isLoaded,
    isLoading,
    deleteSessionTranscript,
    isDeletingTranscript,
    sessionTranscript
  } = useSessionTranscript(sessionId, client?.id, {
    enabled: isOpen,
    initialData: sessionTranscriptData
  })

  const handleDeleteTranscript = () => {
    if (sessionTranscript?.id) {
      deleteSessionTranscript({ id: sessionTranscript.id })
    }
  }

  const handleCopyTranscript = async () => {
    if (!transcript || transcript.length === 0) {
      toast({
        description: 'No transcript to copy',
        status: 'warning',
        isClosable: true,
        duration: 2000
      })
      return
    }

    // Format transcript as plain text
    const formattedText = transcript
      .map(item => {
        const speaker = renderSpeaker(item.speaker, client)
        return `${item.start} ${speaker}: ${item.transcript}`
      })
      .join('\n')

    try {
      await copyToClipboard(formattedText)
      toast({
        description: 'Transcript copied to clipboard',
        status: 'success',
        isClosable: true,
        duration: 2000
      })
    } catch (error) {
      toast({
        description: 'Failed to copy transcript',
        status: 'error',
        isClosable: true,
        duration: 2000
      })
    }
  }

  return (
    <>
      <DialogContainer
        isOpen={isOpen}
        onClose={onClose}
        size={modalSize as unknown as string}
        scrollBehavior="none"
      >
        <Box
          w="100%"
          maxW={{ base: 'full', md: '1000px' }}
          maxH="100vh"
          minH="400px"
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          p="0"
        >
          <DialogHeader
            text="Transcript"
            onClose={onClose}
            hideBorder={true}
            textAlign="center"
          />

          <DialogBody p="0">
            <Box
              p="32px 24px 0 24px"
              overflow="hidden"
              flex="1"
              display="flex"
              flexDirection="column"
            >
              {isLoading && (
                <Flex
                  p="16px"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  flex="1"
                  minH="300px"
                >
                  <Spinner size="lg" />
                </Flex>
              )}

              {isUnavailable && (
                <Flex
                  p="16px"
                  border="1px solid"
                  flex="1"
                  minH="300px"
                  borderColor="pale_gray"
                  borderRadius="8px"
                  justifyContent="center"
                  alignItems="center"
                  color="#757575"
                >
                  <Text>No transcript available</Text>
                </Flex>
              )}

              {isProcessing && (
                <Flex
                  p="48px 32px"
                  flexDirection="column"
                  border="1px solid"
                  borderColor="pale_gray"
                  borderRadius="8px"
                  justifyContent="center"
                  alignItems="center"
                  flex="1"
                  minH="300px"
                >
                  <Text textAlign="center" fontWeight="bold" mb="xxsmall">
                    Transcribing audio...
                  </Text>
                  <Text textAlign="center" color="#757575">
                    This process could take up to 1 minute
                  </Text>
                </Flex>
              )}

              {isLoaded && (
                <Box
                  maxH="60vh"
                  overflowY="auto"
                  sx={{
                    '&::-webkit-scrollbar': {
                      display: 'none'
                    },
                    '-ms-overflow-style': 'none',
                    scrollbarWidth: 'none'
                  }}
                >
                  {transcript &&
                    transcript.length > 0 &&
                    transcript.map(item => {
                      const {
                        transcript: transcriptText,
                        speaker,
                        start
                      } = item
                      return (
                        <Flex key={start} gap="8px" mb={4}>
                          <Text minWidth="65px" color="#C9C9C9" fontSize="sm">
                            {start}
                          </Text>
                          <Text
                            width="100px"
                            fontWeight="430"
                            fontSize="sm"
                            color="#282828"
                          >
                            {renderSpeaker(speaker, client)}
                          </Text>
                          <Text fontSize="sm" flex="1" color="#757575">
                            {transcriptText}
                          </Text>
                        </Flex>
                      )
                    })}
                </Box>
              )}
            </Box>
          </DialogBody>

          {isLoaded && (
            <DialogFooter bg="white">
              <Flex
                w="100%"
                gap="small"
                justifyContent="flex-end"
                alignItems="center"
              >
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteConfirmationOpen(true)}
                  isLoading={isDeletingTranscript}
                  colorScheme="red"
                  borderColor="gray.200"
                  leftIcon={<TrashIcon fill="#EB5164" />}
                  px="medium"
                  h="35px"
                >
                  Delete transcript
                </Button>
                <Button
                  onClick={handleCopyTranscript}
                  bg="primary"
                  color="white"
                  px="large"
                  h="35px"
                >
                  Copy transcript text
                </Button>
              </Flex>
            </DialogFooter>
          )}
        </Box>
      </DialogContainer>

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onConfirm={handleDeleteTranscript}
        isConfirming={isDeletingTranscript}
        header="Delete this transcript?"
        text="Deleting the transcript for this session will disable your ability to change the note type or preferences, use the magic edit feature, and regenerate the note."
        confirmText="Delete transcript"
      />
    </>
  )
}
